import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPendingOrdersAction, openOrderWindow } from "../store/actions/orders";
import { mapOrderWindowData } from "../services/utilities";
import { getDeviceId } from "../utils/deviceId";
import { useSnackbar } from "notistack";

export const usePendingOrders = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const pendings = useSelector((state) => state.orders.arrPendingsData);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    setPendingOrders(pendings);
    setLoading(false);
  }, [pendings]);

  useEffect(() => {
    dispatch(fetchPendingOrdersAction());
  }, [dispatch]);

  const handleModifyOrder = useCallback((order, e) => {
    e.stopPropagation();
    dispatch(openOrderWindow(mapOrderWindowData({
      ...order,
      is_modify: true
    }, order.order_side)));
  }, [dispatch]);

  const deletePendingOrder = useCallback(async (oms_order_id, execution_type, e) => {
    e.stopPropagation();
    try {
      const loginId = localStorage.getItem("login_id");
      const token = localStorage.getItem("auth_token");
      if (!loginId) {
        enqueueSnackbar("User is not authenticated.", { variant: "error" });
      }
      const response = await fetch(
        `/api/v1/orders/${oms_order_id}?client_id=${loginId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-authorization-token": token,
            "x-device-type": "WEB",
            "x-device-id": getDeviceId(),
          },
        }
      );
      const data = await response.json();
      if (data.status === "success") {
        enqueueSnackbar(data.message, { variant: "success" });
        setClicked(false);
      } else {
        enqueueSnackbar(data.message, { variant: "error" });
      }
    } catch (err) {
      setError(err.message || "Failed to delete order.");
    } finally {
      setLoading(false);
    }
  }, []);

  const formatTime = useCallback((timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }, []);

  const handleRowClick = useCallback((order) => {
    setSelectedOrder(order);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  const handleDeleteClick = useCallback(async (order, e) => {
    e.stopPropagation();
    if (clicked) return; // Prevent multiple clicks
    setClicked(true); // Set clicked to true immediately

    try {
      await deletePendingOrder(
        order.oms_order_id,
        order.execution,
        e
      );
    } catch (error) {
      console.error("Error deleting order:", error);
      setClicked(false);
    }
  }, [clicked, deletePendingOrder]);

  return {
    // State
    pendingOrders,
    loading,
    error,
    clicked,
    selectedOrder,
    isPending,

    // Actions
    handleModifyOrder,
    handleDeleteClick,
    handleRowClick,
    handleCloseDetails,
    formatTime,
  };
}; 